import React, { useContext, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import classNames from "classnames";
import Svg from "@natera/material/lib/svg";

import { IntlContext, Language } from "@app/provider";
import ChevronUpIcon from "@app/postBill/assets/svg/chevron-up.svg";
import ChevronDownIcon from "@app/postBill/assets/svg/chevron-down.svg";
import PaidIcon from "@app/postBill/assets/svg/paid-icon.svg";
import CurrentIcon from "@app/postBill/assets/svg/current-icon.svg";
import UpComingIcon from "@app/postBill/assets/svg/upcoming-icon.svg";
import CanceledIcon from "@app/postBill/assets/svg/canceled-icon.svg";
import {
  InstallmentVisualStatus,
  PaymentPlanInstallmentStatus,
} from "@app/postBill/models/apiControllerModel";

import "./paymentSchedule.scss";

export interface PaymentScheduleItem {
  label: string;
  date: string;
  amount: string;
  installmentStatus: InstallmentVisualStatus;
  status: PaymentPlanInstallmentStatus;
}

interface PaymentScheduleProps {
  payments: PaymentScheduleItem[];
}

const messages = defineMessages({
  paymentPlanCardTitle: {
    id: "paymentScheduleCardTitle",
    defaultMessage: "{count} total payments",
  },
  paymentScheduleCardItemLabel: {
    id: "paymentScheduleCardItemLabel",
    defaultMessage: "{paymentPlanNumber} payment",
  },
  paymentScheduleCardPaid: {
    id: "paymentScheduleCardPaid",
    defaultMessage: "Paid",
  },
  paymentScheduleCardCanceled: {
    id: "paymentScheduleCardCanceled",
    defaultMessage: "Canceled",
  },
});

const PaymentSchedule: React.FC<PaymentScheduleProps> = ({ payments }) => {
  const [expanded, setExpanded] = useState(true);
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);

  const getStatusIcon = (status: InstallmentVisualStatus): string => {
    switch (status) {
      case InstallmentVisualStatus.PAID:
        return PaidIcon;
      case InstallmentVisualStatus.CURRENT:
        return CurrentIcon;
      case InstallmentVisualStatus.UP_COMING:
        return UpComingIcon;
      case InstallmentVisualStatus.CANCELLED:
        return CanceledIcon;
      default:
        return "";
    }
  };

  const renderPaymentDate = (date: string) => {
    const locale = currentLanguage === Language.EN ? "en-US" : "es-ES";
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(date).toLocaleDateString(locale, options);
  };

  return (
    <div
      className={classNames("payment-schedule-card", { expanded })}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="payment-schedule-card__header">
        <div className="payment-schedule-card__info">
          <div className="payment-schedule-card__title">
            {intl.formatMessage(messages.paymentPlanCardTitle, {
              count: payments.length,
            })}
          </div>
        </div>
        <div className="payment-schedule-card__expand-icon">
          <Svg content={expanded ? ChevronUpIcon : ChevronDownIcon} />
        </div>
      </div>

      <div
        className="payment-schedule-card__body"
        onClick={(e) => e.stopPropagation()}
      >
        {payments.map((payment, idx) => (
          <div className="payment-schedule-card__item" key={idx}>
            <div className="payment-schedule-card__item-header">
              <div className="payment-schedule-card__label-container">
                <Svg
                  className="payment-schedule-card__icon"
                  content={getStatusIcon(payment.installmentStatus)}
                />
                <span className="payment-schedule-card__label">
                  {intl.formatMessage(messages.paymentScheduleCardItemLabel, {
                    paymentPlanNumber: payment.label,
                  })}
                </span>
              </div>
              <span className="payment-schedule-card__amount">
                {payment.amount}
              </span>
            </div>
            <div className="payment-schedule-card__subtext">
              <span>{renderPaymentDate(payment.date)}</span>
              {payment.status === PaymentPlanInstallmentStatus.PAID && (
                <span className="payment-schedule-card__paid-status">
                  {intl.formatMessage(messages.paymentScheduleCardPaid)}
                </span>
              )}
              {payment.status === PaymentPlanInstallmentStatus.FAILED &&
                payment.installmentStatus ===
                  InstallmentVisualStatus.CANCELLED && (
                  <span className="payment-schedule-card__cancelled-status ">
                    {intl.formatMessage(messages.paymentScheduleCardCanceled)}
                  </span>
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentSchedule;
